/**
 * The vocabulary a latency policy is written in.
 *
 * One file rather than two, because the listing and the form have to agree. A row that
 * reads `3× · 6× · 12×` and a form that writes `3, 6, 12` are the same policy.
 *
 * **Wording, and what the alert type decides. Never the rules.** Whether a ladder is
 * acceptable is the service's answer, asked with the `nf-validate` header before anything
 * is written — see `AlertServiceV2.validateLatencyPolicy`. A copy of those rules here would
 * only be a save blocked by a wording that had drifted from the one that stopped it, and a
 * rule stricter than the service's is a save nobody can complete.
 *
 * It lives beside the form rather than in the page library because the form is where a
 * policy is written, and the listing borrows the wording. `page` may depend on `feature`,
 * so the dependency runs the right way.
 *
 * <h2>Every policy is auto or fixed, and never both</h2>
 *
 * **Auto grades against the subject's own learned baseline**: `3×` is three times what
 * that link normally reads. **Fixed grades against absolute milliseconds** and needs no
 * baseline, so it grades the first measurement it sees.
 *
 * That is a property of every policy, network defaults included — the kind is part of the
 * thresholds rather than part of the key, so a policy can be switched from one to the
 * other in place. A policy with neither grades nothing and the service refuses to store one.
 *
 * <h2>Some alert types take auto and nothing else</h2>
 *
 * An alert type whose condition is a ratio between two windows has nothing for an absolute
 * number to grade, and the service refuses both `fixed` and `auto.ceilingMillis` on a policy
 * written against one. {@link kindsFor} is what a door builds its kind picker from, so the
 * choice is never offered and then refused.
 *
 * The same type also changes what a multiplier means, so it changes the wording rather than
 * only the controls — see {@link comparisonSentence}. The two live here together because a
 * door that filtered the kinds and kept the baseline wording would offer the right control
 * with the wrong sentence under it.
 */

import {
    AlertTypeView,
    AutoThresholds,
    ComparisonWindow,
    ComponentType,
    FixedThresholds,
    LatencyPolicy,
    LatencyPolicyKind,
} from '@netfoundry-ui/shared/model';
import { isEmpty, isNil } from 'lodash';

/**
 * The two ways a policy can grade a measurement, and what choosing one costs.
 *
 * The hints carry the trade the reader is actually making. Auto adapts to each subject and
 * needs a warm-up to do it; fixed works immediately and treats a 3 ms link and a 300 ms one
 * as the same thing.
 */
export const POLICY_KINDS: PolicyKindOption[] = [
    {
        value: 'AUTO',
        label: 'Auto',
        hint: 'Multiples of what each component normally measures. It follows each component, and has to learn a normal level first.',
    },
    {
        value: 'FIXED',
        label: 'Fixed',
        hint: 'A set number of milliseconds, the same for every component. It works from the first measurement, with nothing to learn.',
    },
];

/** One card in a kind picker: what it writes, what it is called, and what picking it costs. */
export interface PolicyKindOption {
    value: LatencyPolicyKind;
    label: string;
    hint: string;
}

/**
 * Whether a policy for one alert type may grade against absolute milliseconds.
 *
 * **Absent means yes.** An older service does not publish the flag, and a registry nobody
 * could read publishes nothing at all — see `AlertTypeView.acceptsFixedThresholds`. Reading
 * absence the other way would hide the fixed form for every type whenever the registry is
 * empty, so a reader whose only fault is a missing permission would lose the step policies
 * with it.
 *
 * A type the registry does not hold answers the same way, which is what the network defaults
 * table needs: it draws a row for every type a policy already exists on, including one the
 * registry has stopped offering.
 */
export function acceptsFixedThresholds(type?: AlertTypeView | null): boolean {
    return type?.acceptsFixedThresholds !== false;
}

/**
 * The kind cards to draw for one alert type, with the wording that type calls for.
 *
 * **Filtered rather than disabled.** A control that cannot be used still asks the reader to
 * work out why, and {@link RATIO_ONLY_NOTE} answers that in a line of prose instead.
 *
 * **The hint comes back with the cards** rather than being overridden by each caller. Auto
 * means something different on a type that compares two windows, and three doors each
 * substituting their own sentence is three chances to draw the right control over the wrong
 * explanation.
 */
export function kindsFor(type?: AlertTypeView | null): PolicyKindOption[] {
    const offered = acceptsFixedThresholds(type)
        ? POLICY_KINDS
        : POLICY_KINDS.filter((entry) => entry.value === 'AUTO');
    const sentence = comparisonSentence(type?.comparisonWindow);
    if (isEmpty(sentence)) {
        return offered;
    }
    return offered.map((entry) => (entry.value === 'AUTO' ? { ...entry, hint: sentence } : entry));
}

/**
 * Why an alert type offers no fixed thresholds, for the reader looking at one card where
 * there are usually two.
 *
 * One line, because what drift is has been said already: {@link DRIFT_NOTE} sits under the
 * type picker, which the reader passes before reaching the kinds. This answers only the
 * question the missing card raises, and it answers it from what drift means rather than
 * from the shape of the request.
 *
 * Names no alert type and no field. The type that does compare absolute milliseconds is a
 * different row in the same picker, and a console that spelled its id here would be one
 * detector rename away from pointing at nothing.
 */
export const RATIO_ONLY_NOTE =
    'A number of milliseconds describes a speed and not a change, so this type takes multiples only.';

/**
 * What a multiplier is a multiple *of*, as a noun phrase for the middle of a sentence.
 *
 * The rules list under an auto ladder states the rule — the multipliers ascend, and each is
 * above one — which is true whichever window they are taken over. Only the phrase naming
 * what they are of changes, so only that phrase is built here.
 *
 * Deliberately loose about the geometry, as {@link comparisonSentence} on the kind card is
 * too: what a reader needs under the boxes is which of the two things a multiple is taken
 * against, and the spans behind it are configuration rather than meaning.
 */
export function multiplierBasis(window?: ComparisonWindow | null): string {
    return isNil(window) ? 'a learned normal level' : 'an earlier window';
}

/**
 * What a multiplier multiplies, for an alert type that compares two windows.
 *
 * **The empty string where there is no window**, which is the ordinary case. The
 * normal-level wording already sits in {@link POLICY_KINDS}, so returning it from here
 * would put the same sentence in two places — and inventing a comparison for a type that
 * makes none would describe something the detector is not doing.
 *
 * **Any window at all is enough**, and none of its numbers are read. All the sentence
 * claims is which two things a multiple is taken between, and that holds for every windowed
 * type whatever spans it publishes — so a window that arrives incomplete still reads as
 * drift rather than falling back to a sentence naming the wrong basis. That is what
 * {@link multiplierBasis} already does, off the same one fact.
 *
 * **The spans are deliberately not in here.** "The 7 days ending 14 days ago" is a
 * geometry, and somebody choosing a threshold is asking what the number means rather than
 * how the detector is configured. The reader who does need the spans has them on a drift
 * alert's own evidence, which carries them under the same names.
 */
export function comparisonSentence(window?: ComparisonWindow | null): string {
    return isNil(window)
        ? ''
        : 'Multiples of what the component measured over an earlier window, compared to the current measured window.';
}

/** The four kinds of component a policy can override, as the service spells them. */
export const COMPONENT_TYPES: { value: ComponentType; label: string }[] = [
    { value: 'SERVICE', label: 'Service' },
    { value: 'EDGE_ROUTER', label: 'Edge router' },
    { value: 'LINK', label: 'Link' },
    { value: 'DETECTOR', label: 'Detector' },
];

/** Kinds only an offline policy takes. Kept out of {@link COMPONENT_TYPES} so the latency filters do not offer them. */
const OFFLINE_ONLY_COMPONENT_TYPES: { value: ComponentType; label: string }[] = [{ value: 'IDENTITY', label: 'Identity' }];

/** The name to show for a component type, or the raw value when it is not a known one. */
export function componentTypeLabel(componentType?: ComponentType | string | null): string {
    if (!componentType) {
        return '';
    }
    return (
        [...COMPONENT_TYPES, ...OFFLINE_ONLY_COMPONENT_TYPES].find((entry) => entry.value === componentType)?.label ||
        `${componentType}`
    );
}

/**
 * Which way a policy grades, read from which field it carries.
 *
 * Derived rather than stored, because that is how the service expresses it: exactly one of
 * `auto` and `fixed` is set and the other is null. Undefined for a policy carrying
 * neither, which the service will not store — so anything that gets one is looking at
 * something that has already gone wrong, and should say so rather than guess.
 */
export function policyKind(policy?: LatencyPolicy | null): LatencyPolicyKind | undefined {
    if (!isNil(policy?.fixed)) {
        return 'FIXED';
    }
    if (!isNil(policy?.auto)) {
        return 'AUTO';
    }
    return undefined;
}

/** The name to show for a kind, or '' when a policy carries neither field. */
export function kindLabel(kind?: LatencyPolicyKind | null): string {
    if (!kind) {
        return '';
    }
    return POLICY_KINDS.find((entry) => entry.value === kind)?.label || `${kind}`;
}

/** One multiplier as it is written everywhere: `3×`, or an em dash when it is not set. */
export function formatMultiplier(multiplier?: number | null): string {
    if (isNil(multiplier)) {
        return '—';
    }
    return `${multiplier}×`;
}

/**
 * One absolute latency, in the milliseconds the service holds it in.
 *
 * Milliseconds throughout, never switched to seconds past a threshold: a column is read
 * down, and a row saying `2 s` beside one saying `250 ms` makes the reader do the
 * conversion the unit was supposed to save them.
 */
export function formatMillis(millis?: number | null): string {
    if (isNil(millis)) {
        return '—';
    }
    return `${millis.toLocaleString()} ms`;
}

/**
 * The three multipliers in the order they fire.
 *
 * Minor, major, critical — the order they ascend in, which is also the order a reader
 * checks them in when asking "how bad does this get before somebody is woken".
 */
export function autoSummary(auto?: AutoThresholds | null): string {
    if (isNil(auto)) {
        return '—';
    }
    return [auto.minorMultiplier, auto.majorMultiplier, auto.criticalMultiplier]
        .map((multiplier) => formatMultiplier(multiplier))
        .join(' · ');
}

/**
 * What each empty box in an auto ladder shows, taken from whatever the box would inherit —
 * the network policy for the alert type, or the platform ladder under it.
 *
 * **A placeholder, never a value to prefill.** Prefilled, a reader who saves without
 * touching the boxes writes a policy that says exactly what the fallback already said, and
 * that policy then stops following the fleet ladder when it moves. As a placeholder the
 * numbers stay what the fleet does, which is the true answer either way — on the network
 * defaults table it is literally what happens if the row is left alone.
 *
 * **The empty string per number the type does not publish**, and for a type with no fleet
 * defaults at all. A placeholder standing in for one alert type's ladder while another type
 * is on screen is worse than an empty box, because a drift row showing a step ladder is the
 * one mistake here that silences a whole alert type.
 *
 * **No ceiling.** It is the one box in the ladder whose empty state means something on its
 * own — no ceiling, a pure ratio — so it says `none` rather than borrowing a number that
 * would read as the value it is about to not use.
 */
export function ladderPlaceholders(inherited?: AutoThresholds | null): LadderPlaceholders {
    return {
        floorMillis: placeholderNumber(inherited?.floorMillis),
        minorMultiplier: placeholderNumber(inherited?.minorMultiplier),
        majorMultiplier: placeholderNumber(inherited?.majorMultiplier),
        criticalMultiplier: placeholderNumber(inherited?.criticalMultiplier),
    };
}

/** The four boxes an auto ladder fills in, named after the fields they write. */
export interface LadderPlaceholders {
    floorMillis: string;
    minorMultiplier: string;
    majorMultiplier: string;
    criticalMultiplier: string;
}

/* Bare digits: the box carries its own unit, as a `ms` or `×` beside it, and a placeholder
 * repeating that would read as part of the number. */
function placeholderNumber(value?: number | null): string {
    if (isNil(value) || Number.isNaN(value)) {
        return '';
    }
    return `${value}`;
}

/**
 * The three fixed rungs in the order they fire.
 *
 * An em dash per rung nobody set rather than a shorter list: an absent rung is
 * **unreachable**, not zero, so a policy carrying only `criticalMillis` opens critical
 * alerts and nothing else. Dropping the gap would make that read as a minor threshold.
 */
export function fixedSummary(fixed?: FixedThresholds | null): string {
    if (isNil(fixed)) {
        return '—';
    }
    return [fixed.minorMillis, fixed.majorMillis, fixed.criticalMillis]
        .map((millis) => formatMillis(millis))
        .join(' · ');
}

/** A policy's ladder, in whichever units its kind is measured in. */
export function thresholdsSummary(policy?: LatencyPolicy | null): string {
    if (policyKind(policy) === 'FIXED') {
        return fixedSummary(policy?.fixed);
    }
    if (policyKind(policy) === 'AUTO') {
        return autoSummary(policy?.auto);
    }
    return '—';
}

/**
 * What a policy applies to, in one line.
 *
 * **Decided by the component id, not the component type.** The type is read-only and not
 * always served — it is not sent on a create — so a policy with an id and no type is an
 * override, and reading the missing type as "no component" would report it as a network
 * policy. That is the one mistake this function exists to prevent, because the two are
 * opposite answers about how far a threshold reaches.
 *
 * The component id is deliberately not in here. It goes on its own line under this one,
 * where it is monospaced — an id is copied and compared rather than read.
 */
export function policyScope(policy: LatencyPolicy): string {
    if (isEmpty(policy.componentId)) {
        return 'Network policy';
    }
    /* Named when the service says what kind it is, and left as a plain override when it
     * does not. "Override" alone is still true; a guessed kind would not be. */
    const kind = componentTypeLabel(policy.componentType);
    return isEmpty(kind) ? 'Component override' : `${kind} override`;
}

/**
 * What to label each id box with, for one alert type's `componentIdParts`.
 *
 * **A repeated kind is lettered.** `link.latency` is keyed by two edge routers, and two
 * boxes both labelled "Edge router" would leave a reader guessing whether the order
 * matters. It does not — the service sorts them — but a form has to say which box is which
 * to be usable at all, so they read "Edge router A" and "Edge router B".
 *
 * A kind that appears once is not lettered: "Service" is clearer than "Service A".
 */
export function componentPartLabels(parts: ComponentType[] = []): string[] {
    const counts = new Map<string, number>();
    for (const part of parts) {
        counts.set(part, (counts.get(part) || 0) + 1);
    }
    const seen = new Map<string, number>();
    return parts.map((part) => {
        const label = componentTypeLabel(part) || 'Component';
        if ((counts.get(part) || 0) < 2) {
            return label;
        }
        const index = (seen.get(part) || 0) + 1;
        seen.set(part, index);
        /* A, B, C… rather than 1, 2, 3: a number beside an id box reads as part of the
         * value, and these are names for the boxes rather than an order to keep. */
        return `${label} ${String.fromCharCode(64 + index)}`;
    });
}

/* Small counts read as words in prose, and as digits past that. "Needs two edge router
 * ids" is a sentence; "Needs 2 edge router ids" is a label that happens to contain one. */
const NUMBER_WORDS = ['no', 'one', 'two', 'three', 'four'];

function countWord(count: number): string {
    return NUMBER_WORDS[count] || `${count}`;
}

/** A list of box labels as a reader says it: "A and B", "A, B and C". */
function joinLabels(labels: string[]): string {
    if (labels.length < 3) {
        return labels.join(' and ');
    }
    return `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`;
}

/**
 * What each alert type this console ships against watches for, keyed by its id.
 *
 * **A stopgap, not a catalogue.** `/alert-types` publishes a name, the component kinds a
 * policy is keyed by, the fleet ladder and the window a multiplier is taken over — but no
 * description. So the one thing a reader wants before choosing a type is the one thing the
 * registry cannot answer, and a name alone does not tell `link.latency` and
 * `service.dial-latency` apart in any way that helps.
 *
 * A type missing from here is not left silent: {@link alertTypeWatches} derives a sentence
 * from the published fields instead, which is plainer but never wrong. That is the whole
 * reason this map is allowed to be incomplete — unlike a table of hand-written *names*,
 * which would hide a name the service does publish.
 *
 * The real fix is a `description` on the alert type, declared by the detector the same way
 * the name already is. Then this map goes, and nothing here needs to know the types.
 */
const ALERT_TYPE_WATCHES: Record<string, string> = {
    'link.latency': 'How fast a link is right now, against what that link normally measures.',
    'service.dial-latency': 'How long it takes to dial a service right now, against what that service normally takes.',
};

/**
 * What the chosen alert type watches for, in one sentence.
 *
 * The declared sentence where there is one, and otherwise the honest reading of the
 * published fields: a type carrying a {@link ComparisonWindow} is asking whether something
 * has drifted, and a type without one is asking how slow it is now. Those are different
 * questions, and the answer says which, because it changes what a multiplier on it means.
 *
 * `''` before a type is chosen, and for a type the registry does not hold, so a caller can
 * leave the block off rather than draw an empty one.
 */
export function alertTypeWatches(type?: AlertTypeView | null): string {
    if (isNil(type)) {
        return '';
    }
    /* A drift type gets {@link DRIFT_NOTE} and nothing else. A sentence of its own in front
     * of it — "whether a link has grown slower over time" — says the same thing twice, and
     * the reader reads the weaker of the two first. */
    if (!isNil(type.comparisonWindow)) {
        return DRIFT_NOTE;
    }
    return ALERT_TYPE_WATCHES[type.alertType] || derivedWatches(type);
}

/**
 * What drift is, for the reader who has just chosen a type that measures it.
 *
 * The whole answer for such a type, keyed off the window rather than written into each
 * drift entry — so the two drift types and any the map does not hold all explain it the
 * same way, and a detector shipping a third needs no change here.
 *
 * It sits under the type because this is where the reader first meets the word. The kind
 * cards below are about which numbers to type, and by then the question has to be settled;
 * {@link RATIO_ONLY_NOTE} says only why one of those cards is missing.
 */
const DRIFT_NOTE =
    'Drift is about change over time. This alert type compares how fast it is now with how fast it was earlier, ' +
    'so what counts is how much it moved rather than how slow it is.';

/* The sentence for a type the map does not hold, off the published fields. Only ever reached
 * for a type with no window: a windowed one is answered by DRIFT_NOTE before this is asked. */
function derivedWatches(type: AlertTypeView): string {
    const noun = subjectNoun(type);
    return `How fast ${articleFor(noun)} ${noun} is right now, against what that ${noun} normally measures.`;
}

/* What one of these is measured on, as a noun for the middle of a sentence. "Component" is
 * the last resort rather than a guess: an alert type about no component at all takes no
 * latency policy, so nothing reaching here has none. */
function subjectNoun(type: AlertTypeView): string {
    return (componentTypeLabel(type.componentType) || 'component').toLowerCase();
}

/* The article for one of those nouns. "Edge router" is the one that needs `an`, and it is
 * a component type rather than a special case — a detector shipping a rule about an
 * ingress or an endpoint would need the same. Sounded from the spelling, which is exact
 * for every noun a component type can produce. */
function articleFor(noun: string): string {
    return /^[aeiou]/.test(noun) ? 'an' : 'a';
}

/**
 * What an alert type is keyed by, as a sentence for the reader choosing it.
 *
 * The useful thing to know before picking a type is how many ids it will ask for and of
 * what — a link policy wants the two routers the link joins, a service policy one service
 * id. Built from `componentIdParts` rather than written per type, so a detector shipping a
 * rule keyed differently describes itself correctly without a change here.
 */
export function keyedBySentence(parts: ComponentType[] = []): string {
    if (parts.length === 0) {
        return 'Not about a component.';
    }
    const counts = new Map<ComponentType, number>();
    for (const part of parts) {
        counts.set(part, (counts.get(part) || 0) + 1);
    }
    const phrases = [...counts.entries()].map(([kind, count]) => {
        const label = (componentTypeLabel(kind) || 'component').toLowerCase();
        return `${countWord(count)} ${label} ${count === 1 ? 'id' : 'ids'}`;
    });
    return `Needs ${phrases.join(' and ')}.`;
}

/**
 * Why a scope would be refused, or an empty list when it would not.
 *
 * **The count is the alert type's, not this form's.** `componentIdParts` says a link policy
 * needs the two routers it joins and a service policy one service id, so the message names
 * how many are wanted rather than assuming one. The wrong count is a 400 at the service,
 * and this is the same rule applied before the request rather than after it.
 *
 * Every box has to be filled, not just one of them: a link keyed by one router is a key
 * nothing can ever look up.
 */
export function validateScope(componentIds: string[] = [], parts: ComponentType[] = []): string[] {
    const filled = componentIds.map((id) => (id || '').trim()).filter((id) => !isEmpty(id));
    if (filled.length === parts.length && parts.length > 0) {
        return [];
    }
    if (parts.length === 0) {
        return ['This alert type is not about a component, so you cannot write a policy for one here.'];
    }
    const labels = componentPartLabels(parts);
    /* **The labels keep their case.** They name the boxes on screen, and
     * {@link componentPartLabels} letters a repeated kind — so lowercasing them turns "Edge
     * router A and Edge router B" into two articles and a reader who cannot tell which box
     * is which. */
    const wanted =
        parts.length === 1
            ? `The ${labels[0].toLowerCase()} id is required.`
            : `${parts.length === 2 ? 'Both' : `All ${countWord(parts.length)}`} ids are required: ${joinLabels(
                  labels
              )}.`;
    return [`${wanted} To set thresholds for the whole network, use the Network Policy section.`];
}
